import { createElement, type ComponentType, type ReactNode } from 'react';
import {
  Download,
  Edit3,
  ExternalLink,
  Eye,
  FolderSymlink,
  Pencil,
  Printer,
  Send,
  Share,
  SquareArrowOutUpRight,
  Trash2,
  type LucideProps,
} from 'lucide-react';
import { filePath, isLifeTimelinePath, isMemoryPath, isTranscriptsPath, writeDirectoryOf } from './file-browser';
import { isEditableFileName } from './file-edit';
// ⚠️ THE THREE NEAR-IDENTICAL GLYPHS IN THIS FILE ARE THREE DIFFERENT LUCIDE VARIANTS, and Figma names
// which one each action gets. They are told apart by their layer names, not by looking at them at 16px:
//   - `move`          → `16/folder-symlink`            = `FolderSymlink`  (a folder with an arrow INSIDE
//     it — `FolderInput`, which we shipped, is the arrow entering the folder from outside)
//   - `open-new-tab`  → `16/square-arrow-out-up-right` = `SquareArrowOutUpRight` (the same three strokes
//     as `ExternalLink` in a different order; the frame is a full square)
//   - `open-wixel`    → `ExternalLink`, LEFT ALONE. A different action, and not in the redesign node —
//     it is not an oversight that it did not move with the other two.
// `test/files-redesign.test.ts` pins all three by name.
// A fourth mark rides along: `share` (grant access — see `onGrantAccess`) is lucide `Share`, the tray with
// the arrow leaving it, NOT `Share2`, the three-node graph. It is the SAME mark the wide row's dedicated
// share `IconButton` draws (redesign §6, and iOS/Android `shareUpload`), because the two presentations are
// two forms of one action and a second glyph would read as a second thing.
//
// The pencil (`onEdit`) is `Pencil` — the plain mark, distinct from `Edit3`, which stays with the wixel
// "Edit in Wixel" link so the two Edits are told apart at a glance.
import type { OverflowMenuAction } from '../components/symphony/ui';
// `openArtifact` is deliberately NOT imported any more: every navigation this module offers is now a real
// anchor, so nothing here calls `window.open`. `test/doc-viewer-ux.test.ts` pins that.
import { downloadArtifact, downloadRemoteFile } from './artifact-file';
import { extractUrl } from './artifact-image';
import { effectiveContent, parseFileRecordContent } from './file-record';
import { wixelEditUrl } from './wixel-link';
import type { DocViewerKind } from './doc-viewer';
import type { WorkspaceFile } from './workspace-files';

/** Where the action set is being rendered:
 *   - `row`     — the file/app row's "..." menu (Files gallery table, cross-workspace Files page). Carries
 *     View, since the row itself doesn't already show the document.
 *   - `viewer`  — the open `DocViewerModal` — its header cluster on a desktop, its kebab sheet on a phone.
 *     No View (it would be redundant with itself).
 *   - `panel`   — the no-viewer fallback (`FileDetailBody`), rendered as inline buttons rather than a menu.
 *     No View (there is no viewer to open).
 *
 *  ⚠️ **`view` is the ONLY entry this discriminant gates any more**, and that is the point of the change that
 *  wrote this sentence. It used to gate `move` / `send-copy` / `share` as well, so `context: 'viewer'`
 *  silently emitted none of them however the caller was wired — which is exactly how the open document's
 *  kebab came to offer three actions while the row behind it offered seven, on all three surfaces at once.
 *  Every other entry is gated by its own CALLBACK (and, for Print, by the kind), so a host that can perform
 *  an action gets it and a host that cannot passes nothing. `panel` still shows only Download /
 *  Open-in-new-tab / Discuss for that reason: `FileDetailBody` passes only `onDiscuss`. */
export type FileActionContext = 'row' | 'viewer' | 'panel';

/** The viewer actions that are FILING rather than reading — what to do with the document, as opposed to how
 *  to get at its bytes.
 *
 *  It exists because the two halves are presented differently at desktop width and identically on a phone.
 *  Figma `15652:57057` draws the desktop viewer's header as the labelled utility trio (Download / Print /
 *  Open in new tab) and nothing else; six labelled buttons beside a file name is not that design. So the
 *  desktop header spells out the utility half and puts THIS half behind a kebab in the same cluster, while
 *  the phone (`15897:424580`) puts the whole set in one bottom sheet — the same wide-popover/narrow-sheet
 *  split `FileRowMenu` already applies to the row, for the same reason.
 *
 *  The set is a KEY list rather than a `context` of its own so that `buildFileActions` keeps returning one
 *  action set per host: the width question is asked once, by the host, and cannot disagree with itself. */
const VIEWER_MANAGE_KEYS: ReadonlySet<string> = new Set(['move', 'send-copy', 'share']);

/** Whether a viewer action belongs to the filing half — see `VIEWER_MANAGE_KEYS`. */
export const isViewerManageAction = (action: OverflowMenuAction): boolean => VIEWER_MANAGE_KEYS.has(action.key);

export interface FileActionCallbacks {
  /** Opens the in-app viewer for this row. Only rendered when `context === 'row'` and `kind` is non-null. */
  onView?: () => void;
  /** Seeds a chat asking about this file. Always present — Discuss is offered for every kind, including
   *  the no-viewer fallback. */
  onDiscuss: () => void;
  /** Opens the "Move to…" dialog. Absent when the caller cannot move the item (a chat attachment has no
   *  folder, and no route of ours can name one).
   *
   *  ⚠️ It used to be gated on `context === 'row'` too, on the stated grounds that "the viewer footer has no
   *  list to put it back into". **That footer no longer exists** — the viewer's actions are its header cluster
   *  and its kebab — and the argument did not survive it. Filing the document you are reading is an ordinary
   *  thing to want, the redesign sheet (`15910:425995`) lists Move to…, and a phone user who opened a file
   *  from a chat has no row to go back to. The gate is now the callback, like every entry below it. */
  onMove?: () => void;
  /**
   * Opens "Send a copy" — a link, a mail draft, or a download. Grants NOBODY anything.
   *
   * A NAMED item rather than a glyph, in every menu that offers it — the row's and the open viewer's alike.
   *
   * ⚠️ **This is the one entry with a real platform exception attached, and it is web-only.** On iOS and
   * Android there is no separate "send a copy": preparing the bytes and handing them to the OS IS the
   * download there (`ArtifactFileAccess.saveToFiles` / `ACTION_SEND`), so the phones deliberately carry one
   * row where web carries two — settled, and written down in `native-apps/CLAUDE.md` ("SETTLED: 'Share'
   * grants access; 'Send a copy' hands over a copy"). Web keeps both because its Download really is a file
   * download and its Send-a-copy really is a link/mail dialog: two different acts, one of which the other
   * cannot perform.
   *
   * ⚠️ It is the one kind of sharing the redesign (§7) KEPT here UNCONDITIONALLY, and the asymmetry is
   * the point. Granting access left the WIDE menu because the wide row already has a dedicated affordance
   * for it — the share `IconButton` beside the ⋮, wired straight to `FileAccessDialog` — so a named entry
   * for it was the same action said twice. Sending a copy has no such affordance at any width: the glyph
   * does NOT do it, and there is no other entry point on the row, so removing the entry removed the
   * action. A menu item is also the only form in which the two can be told apart — a lone share glyph
   * cannot say which of the two things English calls sharing it means, which is exactly how the glyph came
   * to open a copy-sender while the action that changes who may read the file sat in text below it.
   *
   * It sits AFTER "Move to…" and BEFORE "Open in new tab", in every branch that builds a menu.
   */
  onSendCopy?: () => void;
  /**
   * Opens the in-place text editor — the pencil.
   *
   * Offered in BOTH row and viewer contexts (like Download and Print), because editing a file is something
   * you decide either from the list or while reading it, and the viewer is where the editor opens anyway.
   *
   * Two gates, and they are different questions. THIS callback is "can this surface host an editor" — the
   * chat-attachment surfaces pass nothing, since an attachment has no workspace path to write back to.
   * `isEditableFileName` is "is this file text we can hand back as a string", and is checked here so no
   * caller has to remember it. A file whose bytes live on WixMP passes the name test and is refused later,
   * by the editor and again by the endpoint — deliberately, because the name is all a row knows and an
   * affordance that appeared only after a fetch would flicker.
   */
  onEdit?: () => void;
  /**
   * Opens `FileAccessDialog` — who may READ this file. Grants access; hands out no copy.
   *
   * ⚠️ **The rule is "a named entry wherever there is no share glyph to say it instead", and it is gated
   * ENTIRELY on this callback.** Nothing in here reads a width; the host decides, and the two hosts decide
   * differently because they draw different chrome:
   *
   *  - **The ROW — narrow only.** §7 took grant-access out of the row menu on the strength of the wide row's
   *    dedicated share `IconButton`. The mobile Figma draws no such button on a narrow row, and `FilesTable`
   *    / `FilesGrid` duly do not render one below `FILES_FOLD_BREAKPOINT_PX` — so on mobile web the ONLY
   *    remaining path to the dialog was a control that is not there. The action was not moved; it was
   *    deleted, on one width, silently. `FilesBrowser` measures its own pane ONCE and hands the callback over
   *    only when narrow — the same `narrowPane` boolean that suppresses the glyph and that `FileRowMenu`
   *    reads to choose sheet over popover, so the glyph, the item and the presentation can never disagree.
   *  - **The open VIEWER — always.** Its bar is `[✕] [name/subtitle] … [discuss bubble] [kebab]` on a phone
   *    and `[title] … [Download] [Print] [Open in new tab] [kebab] [✕]` on a desktop. Neither draws a share
   *    glyph at any width, so there is no affordance for a named entry to duplicate and the width question
   *    does not arise. Giving somebody access to the report you are reading is the ordinary case, not the
   *    exotic one.
   *
   * Both phones already keep the entry in their own file menu and write the exception down: iOS
   * `DocumentViewer.swift` (`fileActionSheetGrantAccess`) and Android `FileActionMenu.kt`
   * (`MenuGrantAccess`), each LAST in the primary group. This is the same exception on the third surface —
   * see the class doc on `buildFileActions`.
   *
   * Absent callback ⇒ no item, which is what keeps the wide row's popover clean, and what keeps the chat
   * Files panel — whose glyph deliberately DOWNLOADS rather than grants — from growing an action it has
   * never offered.
   *
   * It sits LAST in the primary group, after "Open in new tab", in every branch — the slot both phones use.
   */
  onGrantAccess?: () => void;
  /** Prints the document — offered in BOTH row and viewer contexts (same as Download/Open-in-new-tab),
   *  omitted entirely for the kinds `window.print()` cannot reach (see `PRINTABLE_KINDS`). The row-context
   *  caller is responsible for getting the document on screen first (see `pages/files.tsx`'s wiring).
   *
   *  Web's `PRINTABLE_KINDS` is deliberately WIDER than either phone's printable set (`{pdf, image}` in iOS
   *  `FilePrint.swift` / Android `FilePrint.kt`), and that is a capability difference rather than drift: a
   *  browser can paginate our own DOM, so markdown/CSV/text/the converted Office kinds all reach paper here,
   *  while `UIPrintInteractionController.canPrint` / `PrintManager` only take a document the OS can render. */
  onPrint?: () => void;
  /**
   * Forgets a MEMORY — opens the confirm in front of `/api/delete-file`. The one destructive action any
   * file menu offers, and it is offered for memory ONLY.
   *
   * TWO gates, and this is the one entry where the callback is NOT enough on its own. A host that can
   * delete passes this for every row (it has no cheaper way to say it), so the builder ALSO asks whether
   * the row is under the knowledge root (`isMemoryPath`): a workspace file, a skill or an agent's note
   * never gets the entry however the host is wired. That is deliberate belt-and-braces with the route,
   * which refuses the same paths by name — a menu that offered Delete on a report and then 403'd would be
   * the affordance-then-refusal this module exists to prevent.
   *
   * It sits in its OWN group, LAST — after Discuss — in every branch that offers it, wearing the
   * destructive skin. Both phones draw it the same way (iOS `FileActionSheet`, Android `FileActionMenu`).
   */
  onDelete?: () => void;
}

export interface BuildFileActionsOptions {
  context: FileActionContext;
  t: (key: string, options?: Record<string, unknown>) => string;
  /** The agent's short name, for the "Discuss it with {agentName}" label. */
  agentName: string;
  /** This file's OWN page, already mount-prefixed and new-tab-ready (`fileNewTabLink`), when the caller can
   *  address it. Absent for a chat attachment, which no route of ours can name — see `openInNewTab`. */
  fileHref?: string;
}

const icon = (Glyph: ComponentType<LucideProps>): ReactNode => createElement(Glyph, { size: 16, 'aria-hidden': true });

/** The kinds Print is offered for — i.e. every kind that HAS a printable form. The test is not "is it in our
 *  DOM" but "can we get it onto paper", which is a two-branch answer (see `lib/doc-print.ts`):
 *
 *  - **Our own DOM** — `markdown`, `csv`, `text`, `image`, and the `doc`/`slides` Office kinds (converted to
 *    markdown, so the print block reaches them exactly as it reaches a `.md`). `window.print()` prints these,
 *    with the `@media print` block in `styles/globals.css` isolating the document from the app's chrome and
 *    letting it run to as many pages as it needs.
 *  - **A separate document we can still print** — `pdf`. Its bytes are in an `<embed>` the parent job cannot
 *    paginate, so `PdfDocView` claims the print job and prints the PDF's own document (`printPdfObjectUrl`).
 *    Print used to be withheld here on the grounds that the browser's PDF viewer has its own button — but
 *    that button is inside the embed, so it does not exist in the row menu, and it does not exist at all on
 *    a phone (where the embed is replaced by a hand-off poster).
 *  - `wixel` — a design asset, shown as its preview image in our DOM. That image is what prints; the two
 *    "open in Wixel" links carry `data-print-hide`, since a link is nothing on paper.
 *
 *  Two kinds are deliberately absent, and both absences are about the FILE, not about our plumbing:
 *
 *  - `video` / `audio` — there is no printable form of a recording. Neither native platform prints at all,
 *    so nothing diverges here either.
 *  - `html` — the report is an `<iframe srcDoc sandbox>` with NO `allow-same-origin` (a hard security
 *    requirement pinned by `test/doc-viewer-sandbox.test.ts`), so the parent job renders the frame at its
 *    on-screen box — page one only. Printing it properly means calling `print()` INSIDE that frame, which
 *    needs `allow-modals`, which would also let an agent-authored report raise dialogs attributed to our own
 *    origin. A truncated document and a phishing surface are both worse than the honest route the report
 *    already has: **Open in new tab**, where it is the whole document and the browser's own Print works. */
const PRINTABLE_KINDS: ReadonlySet<DocViewerKind> = new Set<DocViewerKind>([
  'markdown',
  'csv',
  'text',
  'image',
  'doc',
  'slides',
  'pdf',
  'wixel',
]);

/** The "Open in new tab" entry, as a REAL ANCHOR — or nothing, when we have no honest target.
 *
 *  It used to be `onSelect: () => openArtifact(item)`, i.e. `window.open`, which failed four ways: a popup
 *  blocker could swallow it, cmd/ctrl/middle-click could not reach it (there was no href to modify-click), an
 *  inline artifact got a blob URL that `artifact-file.ts` REVOKES after 60s so reloading the tab broke, and a
 *  remote file put its signed WixMP URL into browser history and the referrer.
 *
 *  **"Open in new tab" opens THE FILE, not our page about the file.** Two tiers:
 *
 *   1. **The file's own URL, whenever it has one.** This is what the action says it does. Our own page
 *      renders the *previewer* — the same viewer the user just chose to leave — so pointing a new tab at it
 *      answers a different question than the one asked, and a reader who wants the raw PDF/report gets a
 *      second copy of the reading surface instead. It also means the browser's own PDF/image/video handling,
 *      its Save-as and its Print all work on the real document.
 *   2. **Else our page**, for a file whose body is INLINE (markdown, a CSV written as content) and therefore
 *      has no URL to point at. The page is then the only addressable form of the file — it beats nothing, and
 *      for a `kind === null` inline artifact it at least carries the metadata and Download.
 *
 *  When neither applies the entry is OMITTED rather than rendered dead: an inline file whose page we cannot
 *  name has, literally, nowhere to open. Its previous behaviour was a blob-URL tab that `artifact-file.ts`
 *  revoked 60s later, so a reload of that tab broke — not worth preserving, and Download sits right beside
 *  it. The omission is symmetric across every context, which is the invariant `file-actions.test.ts`
 *  protects.
 *
 *  In practice the entry is present for every file the product can address: every workspace-file surface
 *  passes a `fileHref` (see `fileNewTabLink`), and every chat attachment carries a URL. The one file with
 *  neither is a `vfsPath` attachment, whose `url` is `''` by contract and which no route of ours can name —
 *  a data-contract gap, not an address we could invent. */
function openInNewTab(
  item: WorkspaceFile,
  fileHref: string | undefined,
  label: string,
): OverflowMenuAction | null {
  const action = { key: 'open-new-tab', label, icon: icon(SquareArrowOutUpRight) };
  const remoteUrl = extractUrl(effectiveContent(item));
  if (remoteUrl) return { ...action, href: remoteUrl, external: true, newTab: true };
  if (fileHref) return { ...action, href: fileHref, newTab: true };
  return null;
}

/** The row-level "..." menu's and the open viewer's action set — ONE builder so the two can never drift
 *  apart (see the `buildFileActions` anti-drift test in `file-actions.test.ts`: for a given kind and a given
 *  set of callbacks, the row-context and viewer-context action sets are IDENTICAL minus the View entry).
 *
 *  That anti-drift claim used to be true only vacuously. `move`, `send-copy` and `share` were ALSO gated on
 *  `context === 'row'`, so the viewer could not emit them however it was wired, and the test passed because
 *  its fixture happened to pass no such callbacks. The open document's kebab therefore offered three actions
 *  where the row behind it offered seven — on web, iOS and Android simultaneously. **`context` now gates
 *  `view` and nothing else**; everything else is gated by its callback, so "the viewer is the row minus
 *  View" is a structural fact rather than a coincidence of one fixture.
 *
 *  The ROW menu is **View · Download · Print · Move to… · Send a copy · Open in new tab**, then Discuss
 *  in its own group. SENDING A COPY is in it at every width, because the row's glyph does not do it and
 *  the row offers it nowhere else.
 *
 *  **GRANTING ACCESS is appended last in the primary group whenever the caller passes `onGrantAccess`**, and
 *  the two hosts pass it under different conditions because they draw different chrome — see that callback's
 *  doc. On the row it is a width exception rather than a reversal of §7:
 *
 *   - **Wide** — the row grows a dedicated share `IconButton` beside the ⋮, wired straight to
 *     `FileAccessDialog`. §7 removed the menu entry there because a named item said the same thing twice.
 *   - **Narrow** — the mobile Figma draws no such button, so `FilesTable` / `FilesGrid` render none below
 *     `FILES_FOLD_BREAKPOINT_PX`. With the entry gone from the menu too, mobile web had NO path to the
 *     access dialog at all: the action was deleted on one width rather than moved. Both phones avoided exactly this by keeping the entry — iOS
 *     `DocumentViewer.swift` (`fileActionSheetGrantAccess`) and Android `FileActionMenu.kt`
 *     (`MenuGrantAccess`), both last-in-primary — and both write the exception down. This is the third
 *     surface of that same exception, so the phone row sheet reads
 *     **View · Download · Print · Move to… · Send a copy · Open in new tab · Share ── Discuss**.
 *
 *  **The open VIEWER reads the same list minus View**, and renders it in two shapes off ONE width read
 *  (`DocViewerModal` is where that is decided):
 *
 *   - **Phone sheet — the WHOLE thing, BOTH groups**:
 *     **Download · Print · Move to… · Send a copy · Open in new tab · Share ── Discuss it with {agent}**.
 *     The same shape as the phone row sheet above, and the same shape both phones' own viewers now draw.
 *     Discuss is ALSO the header bubble down there, and that is not the duplication §7 removed: the bubble
 *     is the SHORTCUT, the row is the INVENTORY, and a drawer that swallows the entire action set and then
 *     omits the one action the reader most wants reads as broken rather than as tidy.
 *   - **Desktop cluster — `groups[0]` only**, split by `VIEWER_MANAGE_KEYS` into the labelled utility trio
 *     and the kebab's filing half. Discuss is the FOOTER PRIMARY there — permanently on screen, one Tab
 *     away — so a kebab row really would be redundant, and it stays out STRUCTURALLY: both halves are
 *     filters of `groups[0]`, so the Discuss group never enters the list they filter. No second width read.
 *
 *  The gate is the callback, never a width read in here: this module is presentation-blind, and the ONE
 *  place the width question is asked is `FilesBrowser` — `useContainerNarrow` on the pane that carries the
 *  `files` size container, at `FILES_FOLD_BREAKPOINT_PX`. That single boolean also decides whether the row
 *  renders its share glyph and whether `FileRowMenu` opens the sheet, which is what makes "glyph hidden"
 *  and "item present" the same condition instead of two that can both be false (they could, and were: the
 *  glyph asked the pane at 600 while these two asked the viewport at 720). If a share glyph ever lands on
 *  a narrow row, the fix is to stop passing the callback — not to edit this builder.
 *
 *  `kind === null` is the un-previewable file: Download, then whichever of Move to… / Send a copy the caller
 *  wired, then Open-in-new-tab and Share — no Print, because there is nothing rendered for `window.print()`
 *  to reach. Move is deliberately offered here: where a file LIVES has nothing to do with whether we can
 *  preview it, and an unpreviewable file is exactly one you want to file away.
 *  A real kind adds View (row context only) ahead of Download, and Print (for the `PRINTABLE_KINDS` only)
 *  after Download. `wixel` replaces Download/Open-in-new-tab/Print with Open-in-Wixel / Edit-in-Wixel /
 *  Download-the-preview-image, each omitted when its URL is absent from the header — and offers no Move,
 *  since a design asset addressed by a short link has no folder of ours to move between.
 *  Discuss closes every branch in its own group; whether that group is RENDERED is the host's call.
 *
 *  A MEMORY row (under `knowledge/`) alone grows a THIRD group after Discuss: **Delete**, destructive, the
 *  only irreversible action any file menu offers — see `onDelete`. It also loses the pencil, since the
 *  write routes refuse the directory. Every other row keeps the two-group shape above. */
export function buildFileActions(
  item: WorkspaceFile,
  kind: DocViewerKind | null,
  allCallbacks: FileActionCallbacks,
  { context, t, agentName, fileHref: anyFileHref }: BuildFileActionsOptions,
): OverflowMenuAction[][] {
  // A life timeline row (timeline P1.11) is a read-only VIRTUAL file: it keeps View, Download, Send a copy (a
  // download or a mail draft of its own words, made in the browser), Discuss and Delete (forget), and loses what
  // addresses a STORED file: Move, grant access (a share is granted against a stored file id) and Open in new tab
  // (our file page reads the file-system). One gate here, so the row menu and the viewer agree. Same rule on both
  // phones (no Move, no grant access).
  const virtual = isLifeTimelinePath(filePath(item));
  const callbacks: FileActionCallbacks = virtual ? { ...allCallbacks, onMove: undefined, onGrantAccess: undefined } : allCallbacks;
  const fileHref = anyFileHref;
  const discussGroup: OverflowMenuAction[] = [
    { key: 'discuss', label: t('agentCreations.detail.discuss', { agentName }), onSelect: callbacks.onDiscuss },
  ];

  // Offered for EVERY kind, not just `wixel`: a Wixel short link carries no extension, so a file pointing at
  // one classifies by whatever its name/MIME says (or as `null`) and would never reach the branch below.
  // The pencil. Built once here and pushed into whichever branch runs, so "edit" cannot end up meaning
  // something different in the `null`-kind branch than in the ordinary one. Not offered for `wixel`, whose
  // branch has its own Edit (in Wixel) and whose bytes are a design asset, not text.
  //
  // Withheld for a MEMORY row: memory is changed by talking to the agent, and the write routes' allowlist
  // refuses the `knowledge` directory — so a pencil there would open an editor whose Save is refused. What
  // a memory row gets instead is Delete (`deleteGroup`, below). Same rule on both phones.
  const memory = isMemoryPath(filePath(item));
  // Under `transcripts/` only a meeting's notes.md is editable (the transcript is the room's words), and
  // nothing there moves: the card and the history row address a meeting's files by their path.
  const transcripts = isTranscriptsPath(filePath(item));
  const editText: OverflowMenuAction | null =
    callbacks.onEdit && isEditableFileName(item.name) && !memory && !(transcripts && writeDirectoryOf(filePath(item)) === null)
      ? { key: 'edit', label: t('fileActions.edit'), icon: icon(Pencil), onSelect: callbacks.onEdit }
      : null;

  const editorUrl = wixelEditUrl(effectiveContent(item));
  const editWixel: OverflowMenuAction | null = editorUrl
    ? { key: 'edit-wixel', label: t('fileActions.editInWixel'), icon: icon(Edit3), href: editorUrl, external: true }
    : null;

  // GRANT ACCESS — built once, pushed LAST in the primary group of every branch (the slot iOS
  // `FileActionSheet` and Android `FileActionMenu` give it). Its presence is decided ENTIRELY by whether the
  // caller handed us `onGrantAccess`: `FilesBrowser` does so only when `narrowPane` (the same boolean that
  // suppresses the row's share glyph), while `DocViewerModal` does so unconditionally, its bar carrying no
  // share glyph at any width. The `context === 'row'` half of this gate was removed with the same change that
  // wired the viewer — it made the viewer's callback unreachable, so a host could pass it and get nothing.
  // See `onGrantAccess`.
  const grantAccess: OverflowMenuAction | null = callbacks.onGrantAccess
    ? { key: 'share', label: t('files.access.action'), icon: icon(Share), onSelect: callbacks.onGrantAccess }
    : null;

  // DELETE — memory only, its own group, LAST. Two gates, see `onDelete`: the host's callback AND the
  // row's path. A third group only when it is offered, so a menu without it keeps the two-group shape
  // every existing test pins (`return [primary, discussGroup]` stays the literal last line of each branch).
  const deleteGroup: OverflowMenuAction[] | null =
    callbacks.onDelete && memory
      ? [{ key: 'delete', label: t('files.action.delete'), icon: icon(Trash2), destructive: true, onSelect: callbacks.onDelete }]
      : null;

  if (kind === 'wixel') {
    const { header } = parseFileRecordContent(effectiveContent(item));
    const primary: OverflowMenuAction[] = [];
    if (context === 'row' && callbacks.onView) {
      primary.push({ key: 'view', label: t('agentCreations.detail.view'), icon: icon(Eye), onSelect: callbacks.onView });
    }
    if (header?.wixelViewerUrl?.trim()) {
      primary.push({ key: 'open-wixel', label: t('fileActions.openInWixel'), icon: icon(ExternalLink), href: header.wixelViewerUrl.trim(), external: true });
    }
    if (editWixel) {
      primary.push(editWixel);
    }
    if (header?.wixelPreviewUrl?.trim()) {
      const previewUrl = header.wixelPreviewUrl.trim();
      primary.push({
        key: 'download',
        label: t('packs.download'),
        icon: icon(Download),
        onSelect: () => void downloadRemoteFile(previewUrl, item.name),
      });
      // The preview image IS the printable form of a wixel asset, so Print rides with it — and only with it.
      // Without a preview the view renders a placeholder glyph, and a page with a glyph on it is not a print.
      if (callbacks.onPrint) {
        primary.push({ key: 'print', label: t('fileActions.print'), icon: icon(Printer), onSelect: callbacks.onPrint });
      }
    }
    // Last in the group: this branch offers neither Move nor Open-in-new-tab, so "after Move, before Open
    // in new tab" lands at the end — the same relative position the other two branches give it.
    if (callbacks.onSendCopy) {
      primary.push({ key: 'send-copy', label: t('files.share.title'), icon: icon(Send), onSelect: callbacks.onSendCopy });
    }
    if (grantAccess) primary.push(grantAccess);
    if (deleteGroup) return [primary, discussGroup, deleteGroup];
    return [primary, discussGroup];
  }

  // Never on a virtual row, not even to a URL its words happen to contain.
  const newTab = virtual ? null : openInNewTab(item, fileHref, t('docViewer.openInNewTab'));

  if (kind === null) {
    const group: OverflowMenuAction[] = [];
    // View is offered here too: a file we cannot PREVIEW still opens the same modal, which says so and
    // carries these same actions. Before, `null` meant "this row does something else entirely".
    if (context === 'row' && callbacks.onView) {
      group.push({ key: 'view', label: t('agentCreations.detail.view'), icon: icon(Eye), onSelect: callbacks.onView });
    }
    // A kind we cannot PREVIEW can still be editable text — `.tsv` is the plain case: the classifier does
    // not claim it, but it is a text file and the pencil should open it.
    if (editText) group.push(editText);
    if (editWixel) group.push(editWixel);
    group.push({ key: 'download', label: t('packs.download'), icon: icon(Download), onSelect: () => void downloadArtifact(item) });
    // No Print: there is nothing rendered to print. Move is offered — where a file LIVES has nothing to do
    // with whether we can preview it, and an unpreviewable file is exactly one you want to file away.
    if (callbacks.onMove && !transcripts) {
      group.push({ key: 'move', label: t('files.move.action'), icon: icon(FolderSymlink), onSelect: callbacks.onMove });
    }
    if (callbacks.onSendCopy) {
      group.push({ key: 'send-copy', label: t('files.share.title'), icon: icon(Send), onSelect: callbacks.onSendCopy });
    }
    if (newTab) group.push(newTab);
    if (grantAccess) group.push(grantAccess);
    if (deleteGroup) return [group, discussGroup, deleteGroup];
    return [group, discussGroup];
  }

  const primary: OverflowMenuAction[] = [];
  if (context === 'row' && callbacks.onView) {
    primary.push({ key: 'view', label: t('agentCreations.detail.view'), icon: icon(Eye), onSelect: callbacks.onView });
  }
  if (editText) primary.push(editText);
  if (editWixel) primary.push(editWixel);
  primary.push({ key: 'download', label: t('packs.download'), icon: icon(Download), onSelect: () => void downloadArtifact(item) });
  if (PRINTABLE_KINDS.has(kind) && callbacks.onPrint) {
    primary.push({ key: 'print', label: t('fileActions.print'), icon: icon(Printer), onSelect: callbacks.onPrint });
  }
  if (callbacks.onMove && !transcripts) {
    primary.push({ key: 'move', label: t('files.move.action'), icon: icon(FolderSymlink), onSelect: callbacks.onMove });
  }
  if (callbacks.onSendCopy) {
    primary.push({ key: 'send-copy', label: t('files.share.title'), icon: icon(Send), onSelect: callbacks.onSendCopy });
  }
  if (newTab) primary.push(newTab);
  if (grantAccess) primary.push(grantAccess);

  if (deleteGroup) return [primary, discussGroup, deleteGroup];
  return [primary, discussGroup];
}
